import { authService } from './auth-service.js';

const config = {
    groupId: "2ac0dfde-a4db-4e8a-af91-7fa805271a37"
};

class GraphService {
    constructor() {
        this.msalInstance = authService.msalInstance;
        this.cache = {
            members: null,
            lastFetch: null,
            cacheExpiry: 5 * 60 * 1000, // 5 minutes
            photos: new Map(),
            missingPhotos: new Set()
        };
    }

    async getGroupMembers() {
        try {
            // Return cached data if available and not expired
            if (this.cache.members && 
                (Date.now() - this.cache.lastFetch) < this.cache.cacheExpiry) {
                // Trigger background refresh if cache is older than 1 minute
                if (Date.now() - this.cache.lastFetch > 60000) {
                    this.refreshMembersInBackground();
                }
                return this.cache.members;
            }

            return await this.fetchMembers();
        } catch (error) {
            console.error('Error in getGroupMembers:', error);
            // Return cached data if available, even if expired
            if (this.cache.members) {
                return this.cache.members;
            }
            throw error;
        }
    }

    async fetchMembers() {
        const accessToken = await authService.getAccessToken();
        if (!accessToken) return null;

        try {
            // Request all members at once with a large page size
            const response = await fetch(
                `https://graph.microsoft.com/v1.0/groups/${config.groupId}/members?$select=id,displayName,userPrincipalName,jobTitle,department,mail,mobilePhone,businessPhones&$top=999`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Graph API Error:', errorData);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            const result = {
                members: data.value,
                totalCount: data.value.length,
                hasMore: false
            };

            // Update cache
            this.cache.members = result;
            this.cache.lastFetch = Date.now();

            return result;
        } catch (error) {
            console.error('Error fetching members:', error);
            throw error;
        }
    }

    async refreshMembersInBackground() {
        try {
            const result = await this.fetchMembers();
            // Only update the cache if we successfully fetched new data
            if (result) {
                this.cache.members = result;
                this.cache.lastFetch = Date.now();
                // Dispatch an event to notify the UI
                window.dispatchEvent(new CustomEvent('contactsUpdated', { 
                    detail: result 
                }));
            }
        } catch (error) {
            console.error('Background refresh failed:', error);
        }
    }

    async getUserPhoto(userId) {
        try {
            // Check if we know this user has no photo
            if (this.cache.missingPhotos.has(userId)) {
                return null;
            }

            // Check if we have a cached photo URL
            if (this.cache.photos.has(userId)) {
                return this.cache.photos.get(userId);
            }

            const accessToken = await authService.getAccessToken();
            if (!accessToken) {
                return null;
            }

            const response = await fetch(
                `https://graph.microsoft.com/v1.0/users/${userId}/photo/$value`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            // If photo doesn't exist, cache this information and return null
            if (response.status === 404) {
                this.cache.missingPhotos.add(userId);
                return null;
            }

            // For other non-200 responses, return null but don't cache
            if (!response.ok) {
                return null;
            }

            const blob = await response.blob();
            const photoUrl = URL.createObjectURL(blob);
            
            // Cache the photo URL
            this.cache.photos.set(userId, photoUrl);

            return photoUrl;
        } catch (error) {
            // Only log critical errors, not 404s
            if (error.name !== 'TypeError' && !error.message.includes('404')) {
                console.error('Error fetching user photo:', error);
            }
            return null;
        }
    }

    async getUserProfile() {
        try {
            const accessToken = await authService.getAccessToken();
            if (!accessToken) {
                return null;
            }

            const response = await fetch(
                'https://graph.microsoft.com/v1.0/me',
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching user profile:', error);
            throw error;
        }
    }
}

export const graphService = new GraphService(); 